import {useCallback, useRef, useState} from "react";
import {recordDiscountPresented} from "upsell/offerDismissal";
import {createOfferShownLog, offerEvents} from "upsell/offerEvents";
import type {CurrentLadderGift} from "./currentLadderOffer";
import {couponEventDetails} from "./couponEvents";

/**
 * Held by the transfer frame, so tucking and reopening, or closing and reopening the ticket's dialog on a phone
 * (ui#3343), cannot duplicate an impression or reset a claim.
 *
 * Presenting the coupon records the day's discount for the CROSS-SURFACE stores in `upsell/offerDismissal`
 * — the exit offer reads the session record, and tomorrow's success card reads the UTC-day one. It used
 * to notify the frame as well, which silenced the campaign offer on the same screen; that suppression is
 * gone (api#1704, owner 2026-09-15) because the campaign outranks this coupon in the one slot they share.
 */
export function useCurrentLadderGiftActions(gift: CurrentLadderGift | null, accountKey: string | null = null) {
	const shown = useRef(createOfferShownLog());
	const [dialogGift, setDialogGift] = useState<CurrentLadderGift | null>(null);
	const restoreFocus = useRef<(() => void) | undefined>(undefined);
	const closeDialog = useCallback(() => {
		setDialogGift(null);
		restoreFocus.current?.();
	}, []);
	const [claimedId, setClaimedId] = useState<string | null>(null);
	const present = useCallback(() => {
		if (gift === null || !shown.current.shouldReport("SUCCESS_CARD", gift.offer.presentationId)) return;
		const {context, price} = couponEventDetails(gift);
		recordDiscountPresented(accountKey);
		offerEvents.coupon("OFFER_SHOWN", context, price);
		offerEvents.coupon("PRICE_SEEN", context, price);
	}, [gift, accountKey]);
	const claim = useCallback((onClose?: () => void) => {
		if (gift === null) return;
		setClaimedId(gift.offer.presentationId);
		setDialogGift(gift);
		restoreFocus.current = onClose;
		const {context, price} = couponEventDetails(gift);
		offerEvents.coupon("OFFER_CLICKED", context, price);
	}, [gift]);
	const dismiss = useCallback(() => {
		if (gift === null) return;
		const {context, price} = couponEventDetails(gift);
		offerEvents.coupon("OFFER_DISMISSED", context, price);
	}, [gift]);
	// The checkout step is the claim dialog's to report, beside the mint (`./useCouponClaim`).
	return {dialogGift, closeDialog, claimed: claimedId !== null && claimedId === gift?.offer.presentationId, claim, present, dismiss};
}
